import React, { useEffect, useState, useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Armement d'un engin — visuel prototype (`ficheEngin`).
 * Données réelles : `/buildings/vehicules/:id/crew` (compétences requises + bonus).
 * L'équipage du prochain départ se lit directement dans l'armement (colonne
 * « couverte par »), on ne réaffiche donc plus la liste d'équipage ni le
 * personnel disponible du bâtiment.
 */

interface CoveredBy {
  staff_id: number;
  nom: string;
  prenom: string;
  grade_code?: string;
}
interface CompetenceRow {
  config_competence_id: number;
  nom: string;
  covered_by?: CoveredBy | null;
}
interface CrewBonus {
  type: string;
  valeur: number;
  premium: boolean;
}

interface VehiculeCrewSectionProps {
  vehiculeId: number;
}

const VehiculeCrewSection: React.FC<VehiculeCrewSectionProps> = ({ vehiculeId }) => {
  const [requiredComps, setRequiredComps] = useState<CompetenceRow[]>([]);
  const [bonuses, setBonuses] = useState<CrewBonus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}/crew`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || 'Erreur chargement armement');
        return;
      }
      setRequiredComps(data.data.required_competences || []);
      setBonuses(data.data.crew_bonuses || []);
    } catch {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [vehiculeId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <p className="bsec">Armement <span className="muted">compétences requises</span></p>
      {loading ? (
        <div className="empty">Chargement de l'armement…</div>
      ) : requiredComps.length ? (
        <div className="ulist">
          {requiredComps.map((c) => (
            <div className="urow" key={c.config_competence_id}>
              <span className="urow__call">{c.nom}</span>
              <span className={`urow__grow ${c.covered_by ? '' : 'muted'}`}>
                {c.covered_by ? `${c.covered_by.grade_code || ''} ${c.covered_by.prenom} ${c.covered_by.nom}`.trim() : '—'}
              </span>
              {c.covered_by ? (
                <span className="cbadge cbadge--on">Couverte</span>
              ) : (
                <span className="cbadge cbadge--off">Manquante</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="bsub">Aucune compétence obligatoire : cet engin part en accompagnement.</p>
      )}

      {error ? <p className="bwarn">⚠ {error}</p> : null}

      {bonuses.length ? (
        <>
          <p className="bsec">Bonus apportés par cet équipage</p>
          <div className="bonuslist">
            {bonuses.map((b, i) => (
              <div className={`bline ${b.premium ? 'is-prem' : ''}`} key={i}>
                <span className="bline__ico">•</span>
                <span className="bline__nom">{b.type}</span>
                <span className="bline__val mono">+{b.valeur}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
};

export default VehiculeCrewSection;
